import {PolyScene} from '../../../../@polygonjs/polygonjs/src/engine/scene/PolyScene';

import {EngineNodeData, EngineParamData} from '../modules/Engine';
import {BaseNodeType, EmitDataByNodeEventMap} from '../../../../@polygonjs/polygonjs/src/engine/nodes/_Base';
import {BaseParamType} from '../../../../@polygonjs/polygonjs/src/engine/params/_Base';
import {NodeEvent} from '../../../../@polygonjs/polygonjs/src/engine/poly/NodeEvent';
import {CoreGraphNodeId} from '../../../../@polygonjs/polygonjs/src/core/graph/CoreGraph';
import {Ref, ref} from 'vue';
import {PolyDictionary} from '../../../../@polygonjs/polygonjs/src/types/GlobalTypes';
import {StoreController} from './StoreController';
import {ArrayUtils} from '../../../../@polygonjs/polygonjs/src/core/ArrayUtils';

export class EngineStoreControllerClass {
	private _scene!: PolyScene;
	private _frame: Ref<number> = ref(0);
	private _maxFrame: Ref<number> = ref(0);
	private _maxFrameLocked: Ref<boolean> = ref(false);
	private _realtimeState: Ref<boolean> = ref(false);
	private _playingState: Ref<boolean> = ref(false);
	private _nodes_by_graph_node_id: Ref<PolyDictionary<EngineNodeData>> = ref({});
	private _params: Ref<PolyDictionary<EngineParamData>> = ref({});

	private static _instance: EngineStoreControllerClass;
	static instance() {
		return (this._instance = this._instance || new EngineStoreControllerClass());
	}

	private constructor() {}
	set_scene(scene: PolyScene) {
		this._scene = scene;
		this.update_scene();
	}
	get scene() {
		return this._scene;
	}
	// set_store(store: Store<State>) {
	// 	this.update_scene();
	// }

	node(id: CoreGraphNodeId): BaseNodeType | null {
		return this._scene.graph.nodeFromId(id) as BaseNodeType;
	}
	param(id: CoreGraphNodeId): BaseParamType | null {
		return this._scene.graph.nodeFromId(id) as BaseParamType;
	}

	// getters
	json_node(id: CoreGraphNodeId): EngineNodeData | null {
		return this._nodes_by_graph_node_id.value[id];
	}
	json_param(id: CoreGraphNodeId): EngineParamData | null {
		return this._params.value[id];
	}
	json_children(id: CoreGraphNodeId): EngineNodeData[] {
		const json_node = this.json_node(id);
		if (json_node) {
			const children_ids = json_node.children;
			const children = children_ids.map((children_id) => this._nodes_by_graph_node_id.value[children_id]);

			// check if any resolves to null
			const compacted_children = ArrayUtils.compact(children);
			if (compacted_children.length !== children.length) {
				console.warn('some children resolved to null in engine/json_children!', children);
			}
			// updated_children_ids = lodash_map( compacted_children, (child)->child.graph_node_id )
			// POLY.store.commit 'engine/json_children_ids',
			// 	json_node: json_node
			// 	children_ids: updated_children_ids

			return compacted_children;
		} else {
			return [];
		}
	}
	frame(): number {
		return this._frame.value;
	}
	maxFrame(): number {
		return this._maxFrame.value;
	}
	maxFrameLocked(): boolean {
		return this._maxFrameLocked.value;
	}
	realtimeState(): boolean {
		return this._realtimeState.value;
	}
	playing_state(): boolean {
		return this._playingState.value;
	}

	// mutations
	// scene mutations
	update_scene() {
		this.update_frame();
		this.update_frame_range();
		this.update_realtimeState();
		this.update_play_state();

		const include_node_param_components = false;
		for (let node of this._scene.nodesController.allNodes()) {
			this._nodes_by_graph_node_id.value[node.graphNodeId()] = node.toJSON(include_node_param_components);
			StoreController.editor.user_plan.incrementNodesCount();
		}

		this._params.value = {}; //json.params_by_graph_node_id;
	}
	update_frame() {
		this._frame.value = this._scene.frame();
		// this._store.commit(`engine/${EngineMutation.SCENE_FRAME_UPDATED}`);
	}
	update_frame_range() {
		this._maxFrame.value = this._scene.timeController.maxFrame();
		this._maxFrameLocked.value = this._scene.timeController.maxFrameLocked();
		// this._store.commit(`engine/${EngineMutation.SCENE_FRAME_RANGE_UPDATED}`);
	}
	update_realtimeState() {
		this._realtimeState.value = this._scene.timeController.realtimeState();
	}
	update_play_state() {
		this._playingState.value = this._scene.timeController.playing();
	}

	// node mutations
	private _nodeAndJson(node_id: CoreGraphNodeId) {
		const node = this.node(node_id);
		const jsonNode = this.json_node(node_id);
		return {node, jsonNode};
	}
	update_node_error(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		jsonNode.error_message = node.states.error.message();
	}
	update_node_ui_data_position(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		jsonNode.ui_data_json.x = node.uiData.position().x;
		jsonNode.ui_data_json.y = node.uiData.position().y;
	}
	update_node_ui_data_comment(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		jsonNode.ui_data_json.comment = node.uiData.comment();
	}
	update_node_selection(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		if (!(node.childrenAllowed() && node.childrenController)) {
			return;
		}
		if (!jsonNode.selection) {
			return;
		}
		while (jsonNode.selection.pop()) {}
		const new_ids = node.childrenController.selection.toJSON();
		for (let id of new_ids) {
			jsonNode.selection.push(id);
		}
	}
	update_node_display_flag(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		if (node.flags == null) {
			return;
		}
		if (node.flags.display == null) {
			return;
		}
		if (jsonNode.flags == null) {
			return;
		}
		if (jsonNode.flags.display == null) {
			return;
		}
		jsonNode.flags.display = node.flags.display.active();
	}
	update_node_bypass_flag(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		if (node.flags == null) {
			return;
		}
		if (node.flags.bypass == null) {
			return;
		}
		if (jsonNode.flags == null) {
			return;
		}
		if (jsonNode.flags.bypass == null) {
			return;
		}
		jsonNode.flags.bypass = node.flags.bypass.active();
	}
	update_node_optimize_flag(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		if (node.flags == null) {
			return;
		}
		if (node.flags.optimize == null) {
			return;
		}
		if (jsonNode.flags == null) {
			return;
		}
		if (jsonNode.flags.optimize == null) {
			return;
		}
		jsonNode.flags.optimize = node.flags.optimize.active();
	}
	update_node_name(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		jsonNode.name = node.name();
	}
	update_node_inputs(node_id: CoreGraphNodeId) {
		this._updateNodeInputsAndOutputs(node_id);
	}
	update_node_clonable_state(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		jsonNode.override_cloned_state_allowed = node.io.inputs.overrideClonedStateAllowed();
		jsonNode.inputs_clone_required_states = node.io.inputs.cloneRequiredStates();
	}
	update_node_named_inputs(node_id: CoreGraphNodeId) {
		this._updateNodeInputsAndOutputs(node_id);
	}
	update_node_named_outputs(node_id: CoreGraphNodeId) {
		this._updateNodeInputsAndOutputs(node_id);
	}
	addNodeParamsToStore(nodeId: CoreGraphNodeId) {
		const node = this.node(nodeId);
		if (!node) {
			return;
		}
		for (let param of node.params.all) {
			const paramId = param.graphNodeId();
			if (!this._params.value[paramId]) {
				this._params.value[paramId] = param.toJSON();
				if (param.components) {
					for (let component of param.components) {
						const componentId = component.graphNodeId();
						this._params.value[componentId] = component.toJSON();
					}
				}
			}
		}
	}
	updateParams(nodeId: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(nodeId);
		if (!(node && jsonNode)) {
			return;
		}
		const include_node_param_components = false;
		const newIds = node.serializer.to_json_params(include_node_param_components);
		// const idsToKeep = ArrayUtils.intersection(jsonNode.param_ids, newIds);
		const idsToRemove = ArrayUtils.difference(jsonNode.param_ids, newIds);
		for (let param_id of idsToRemove) {
			delete this._params.value[param_id];
		}

		jsonNode.param_ids = newIds;
		for (let param of node.params.all) {
			const paramId = param.graphNodeId();
			this._params.value[paramId] = this._params.value[paramId] || param.toJSON();
			if (param.components) {
				for (let component of param.components) {
					const componentId = component.graphNodeId();
					this._params.value[componentId] = this._params.value[componentId] || component.toJSON();
				}
			}
		}
	}
	add_node(node_id: CoreGraphNodeId, data: EmitDataByNodeEventMap[NodeEvent.CREATED]) {
		// this._commit(EngineMutation.NODE_CREATED, {parent_id: node_id, child_node_json: data.child_node_json});
		StoreController.editor.user_plan.incrementNodesCount();

		const parent_node_id = node_id;
		const parent_node = StoreController.engine.node(parent_node_id);
		if (parent_node) {
			const child_node_json = data.child_node_json;

			this._nodes_by_graph_node_id.value[child_node_json.graph_node_id] = child_node_json;
			const parent_data = this._nodes_by_graph_node_id.value[parent_node.graphNodeId()];
			parent_data.children.push(child_node_json.graph_node_id);
		}
	}
	delete_node(node_id: CoreGraphNodeId, data: EmitDataByNodeEventMap[NodeEvent.DELETED]) {
		StoreController.editor.user_plan.decrementNodesCount();

		const parent_node_id = data.parent_id;
		const parent_data = this._nodes_by_graph_node_id.value[parent_node_id];
		if (parent_data) {
			const child_id = node_id;

			const index = parent_data.children.indexOf(child_id);
			parent_data.children.splice(index, 1);

			delete this._nodes_by_graph_node_id.value[child_id];
			// Vue.delete(state.nodes_by_graph_node_id, child_id);
		}
	}

	// params
	private _paramAndJson(param_id: CoreGraphNodeId) {
		const param = StoreController.engine.param(param_id);
		const paramJson = this._params.value[param_id];
		return {param, paramJson};
	}
	update_param_raw_input(param_id: CoreGraphNodeId) {
		const {param, paramJson} = this._paramAndJson(param_id);
		if (!(param && paramJson)) {
			return;
		}
		paramJson.raw_input = param.serializer.rawInput();
	}
	update_param_value(param_id: CoreGraphNodeId) {
		const {param, paramJson} = this._paramAndJson(param_id);
		if (!(param && paramJson)) {
			return;
		}
		paramJson.value = param.serializer.value();
	}
	update_param_expression(param_id: CoreGraphNodeId) {
		const {param, paramJson} = this._paramAndJson(param_id);
		if (!(param && paramJson)) {
			return;
		}
		paramJson.expression = param.serializer.expression();
	}
	update_param_error(param_id: CoreGraphNodeId) {
		const {param, paramJson} = this._paramAndJson(param_id);
		if (!(param && paramJson)) {
			return;
		}
		paramJson.error_message = param.serializer.error_message();
	}
	update_param_visible_state(param_id: CoreGraphNodeId) {
		const {param, paramJson} = this._paramAndJson(param_id);
		if (!(param && paramJson)) {
			return;
		}
		paramJson.is_visible = param.serializer.is_visible();
	}
	update_param_editable_state(param_id: CoreGraphNodeId) {
		const {param, paramJson} = this._paramAndJson(param_id);
		if (!(param && paramJson)) {
			return;
		}
		paramJson.editable = param.serializer.editable();
	}

	private _updateNodeInputsAndOutputs(node_id: CoreGraphNodeId) {
		const {node, jsonNode} = this._nodeAndJson(node_id);
		if (!(node && jsonNode)) {
			return;
		}
		jsonNode.maxInputsCount = node.serializer.maxInputsCount();
		jsonNode.inputs = node.serializer.inputIds();
		jsonNode.input_connection_output_indices = node.serializer.inputConnectionOutputIndices();
		jsonNode.named_input_connection_points = node.serializer.namedInputConnectionPoints();
		jsonNode.named_output_connection_points = node.serializer.namedOutputConnectionPoints();
	}
}

export const EngineStoreController = EngineStoreControllerClass.instance();
